import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrgButton, ButtonVariant, CommonInput, INPUT_TYPES } from '@brg/ui';
import styled from 'styled-components';
import { LayoutConst, useModal } from 'core';
import { Switcher } from 'shared/modals/ApplicationEditor/components/Inputs/Switcher';
import { LMSLessonClassTypeModel } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';

const ModalWrapper = styled.div`
  width: 720px;
  max-width: calc(100vw - 32px);
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  background: #ffffff;
  border-radius: 8px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
  color: #151514;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  font-size: 36px;
  font-weight: 300;
  line-height: 24px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ui-input_container {
    height: 48px !important;
    background: #f2f2f2;
  }

  .brg-input__control {
    display: flex !important;
    align-items: center !important;
    min-height: 48px !important;
    height: 48px !important;
  }

  .brg-input__value-container,
  .brg-input__indicators {
    display: flex !important;
    align-items: center !important;
  }

  .brg-input__placeholder,
  .brg-input__single-value {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    transform: none !important;
  }

  .brg-input__menu {
    height: auto !important;
    min-height: 0 !important;
    max-height: 240px;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    .ui-input_container {
      height: 40px !important;
    }

    .brg-input__control {
      min-height: 40px !important;
      height: 40px !important;
    }
  }
`;

const Label = styled.label`
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
`;

const RecurringRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const RecurringLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909090;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
`;

interface LessonTypeOption {
  value: string;
  label: string;
}

export interface LessonTypeSelectModalProps {
  lessonTypeOptions: LessonTypeOption[];
  onNext: (lessonType: LMSLessonClassTypeModel, isRecurring: boolean) => void;
}

export const LessonTypeSelectModal: FC<LessonTypeSelectModalProps> = ({ lessonTypeOptions, onNext }) => {
  const { t } = useTranslation();
  const modal = useModal();
  const [lessonType, setLessonType] = useState<LMSLessonClassTypeModel | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);

  const selectableOptions = useMemo(
    () => lessonTypeOptions.filter((option) => Number(option.value) !== LMSLessonClassTypeModel.Ordinal),
    [lessonTypeOptions],
  );

  const selectedOption = selectableOptions.find((option) => Number(option.value) === Number(lessonType));

  return (
    <ModalWrapper>
      <Content>
        <Header>
          <Title>{t('lms.lessonsEditor.createLessonTitle')}</Title>
          <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </Header>

        <Field>
          <Label>{t('lms.lessonsEditor.chooseLessonType')}</Label>
          <CommonInput
            type={INPUT_TYPES.dropdown}
            orientation="column"
            options={selectableOptions as unknown as Record<string, string>[]}
            value={selectedOption}
            placeholder={t('lms.lessonsEditor.lessonTypes.ordinal')}
            onChange={(option: any) => {
              const nextValue = option?.value ?? option;
              if (nextValue === undefined || nextValue === null || nextValue === '') {
                setLessonType(null);
                return;
              }
              setLessonType(Number(nextValue) as LMSLessonClassTypeModel);
            }}
          />
        </Field>

        <RecurringRow>
          <Switcher
            value={Number(isRecurring)}
            label=""
            onChange={(value) => setIsRecurring(Boolean(value))}
          />
          <RecurringLabel>
            {t('lms.lessonsEditor.createRecurringLessons')}
          </RecurringLabel>
        </RecurringRow>
      </Content>

      <Actions>
        <BrgButton fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
          {t('lms.lessonsEditor.cancel')}
        </BrgButton>
        <BrgButton
          fullWidth
          variant={ButtonVariant.Filled}
          disabled={lessonType === null}
          onClick={() => {
            if (lessonType === null) return;
            onNext(lessonType, isRecurring);
          }}
        >
          {t('lms.lessonsEditor.next')}
        </BrgButton>
      </Actions>
    </ModalWrapper>
  );
};
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrgButton, ButtonVariant, CommonInput, INPUT_TYPES } from '@brg/ui';
import styled from 'styled-components';
import { LayoutConst, useModal } from 'core';
import { Switcher } from 'shared/modals/ApplicationEditor/components/Inputs/Switcher';
import { LMSLessonClassTypeModel } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';

const ModalWrapper = styled.div`
  width: 720px;
  max-width: calc(100vw - 32px);
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  background: #ffffff;
  border-radius: 8px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
  color: #151514;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  font-size: 36px;
  font-weight: 300;
  line-height: 24px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ui-input_container {
    height: 48px !important;
    background: #f2f2f2;
  }

  .brg-input__control {
    display: flex !important;
    align-items: center !important;
    min-height: 48px !important;
    height: 48px !important;
  }

  .brg-input__value-container,
  .brg-input__indicators {
    display: flex !important;
    align-items: center !important;
  }

  .brg-input__placeholder,
  .brg-input__single-value {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    transform: none !important;
  }

  .brg-input__menu {
    height: auto !important;
    min-height: 0 !important;
    max-height: 240px;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    .ui-input_container {
      height: 40px !important;
    }

    .brg-input__control {
      min-height: 40px !important;
      height: 40px !important;
    }
  }
`;

const Label = styled.label`
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
`;

const RecurringRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const RecurringLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909090;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
`;

interface LessonTypeOption {
  value: string;
  label: string;
}

export interface LessonTypeSelectModalProps {
  lessonTypeOptions: LessonTypeOption[];
  onNext: (lessonType: LMSLessonClassTypeModel, isRecurring: boolean) => void;
}

export const LessonTypeSelectModal: FC<LessonTypeSelectModalProps> = ({ lessonTypeOptions, onNext }) => {
  const { t } = useTranslation();
  const modal = useModal();
  const [lessonType, setLessonType] = useState<LMSLessonClassTypeModel | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);

  const selectableOptions = useMemo(
    () => lessonTypeOptions.filter((option) => Number(option.value) !== LMSLessonClassTypeModel.Ordinal),
    [lessonTypeOptions],
  );

  const selectedOption = selectableOptions.find((option) => Number(option.value) === Number(lessonType));

  return (
    <ModalWrapper>
      <Content>
        <Header>
          <Title>{t('lms.lessonsEditor.createLessonTitle')}</Title>
          <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </Header>

        <Field>
          <Label>{t('lms.lessonsEditor.chooseLessonType')}</Label>
          <CommonInput
            type={INPUT_TYPES.dropdown}
            orientation="column"
            options={selectableOptions as unknown as Record<string, string>[]}
            value={selectedOption}
            placeholder={t('lms.lessonsEditor.lessonTypes.ordinal')}
            onChange={(option: any) => {
              const nextValue = option?.value ?? option;
              if (nextValue === undefined || nextValue === null || nextValue === '') {
                setLessonType(null);
                return;
              }
              setLessonType(Number(nextValue) as LMSLessonClassTypeModel);
            }}
          />
        </Field>

        <RecurringRow>
          <Switcher
            value={Number(isRecurring)}
            label=""
            onChange={(value) => setIsRecurring(Boolean(value))}
          />
          <RecurringLabel>
            {t('lms.lessonsEditor.createRecurringLessons')}
          </RecurringLabel>
        </RecurringRow>
      </Content>

      <Actions>
        <BrgButton fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
          {t('lms.lessonsEditor.cancel')}
        </BrgButton>
        <BrgButton
          fullWidth
          variant={ButtonVariant.Filled}
          disabled={lessonType === null}
          onClick={() => {
            if (lessonType === null) return;
            onNext(lessonType, isRecurring);
          }}
        >
          {t('lms.lessonsEditor.next')}
        </BrgButton>
      </Actions>
    </ModalWrapper>
  );
};
